<template>
  <div class="f-v f-ast">
    <donut-modal-sections-title :title="`DM a ${$i18n.t('shoutouts.shoutout', { count: 1 })}`" />
    <div v-emoji="descriptionCopy" class="t" />
    <div class="t">{{ $i18n.t('shoutouts.feature') }} admins will be able to see these.</div>
    <div class="f-sb f-gap-40 m-tx3">
      <div class="flexed">
        <!-- Recipients -->
        <div class="t">People to recognize</div>
        <donut-selector-prefabs-user
          v-model="recipientsSelection"
          :is-multi="true"
          :exclude-inactive-wallets="true"
          :excluded-ids="[state.currentUser.id]"
          class="m-t"
        />
        <!-- Send As -->
        <div class="t f-ac m-tx2">
          <span>Send message as...</span>
          <i v-tooltip="'Replies will go to the sender of the message.'" class="mi m-l2 md-14 gray">info</i>
        </div>
        <donut-selector-single-static v-model="sendAsSelection" :options="sendAsOptions" class="m-t" />
        <!-- Message -->
        <div class="t m-tx2">Message</div>
        <donut-input-text
          v-model="message"
          class="m-t h-x20"
          :placeholder="messagePlaceholder"
          :autocomplete="['emoji', 'users', 'channels', 'variables']"
          :textarea="true"
          :max-length="2500"
          :full-height-text-area="true"
          :wysiwyg-ignore="['quote']"
        />
        <button class="inv w-100p m-tx2" @click="saveProgressAndMoveToGifModal">
          <i class="mi m-r">image</i>
          <span>Add GIF</span>
        </button>
        <!-- Points -->
        <div v-emoji="pointsHeader" class="t m-tx2" />
        <div class="f m-t">
          <input v-model="pointsSelection" type="number" :min="1" :step="1" class="w-x8">
          <div v-emoji="`:${pointsConfig.emoji}:`" class="t m-l" />
        </div>
        <div v-if="wallet" v-emoji="pointsSubtext" :class="`t m-t ${notEnoughPoints ? 'red' : 'gray'}`" />
        <div v-else class="t gray m-t">...</div> <!-- Don't want the height of the modal to change once data is fetched -->
      </div>
      <div class="flexed">
        <donut-slack-preview :message="previewMessage" :message-sender="sendingAsUser ? state.currentUser : null">
          <div v-if="gifUrl" class="block">
            <img class="w-100p" :src="gifUrl">
          </div>
          <div v-if="sendingAsUser" class="block">
            <div v-emoji="`You received ${numberedPointsPhrase(pointsSelection)} with this message.`" class="context" />
          </div>
        </donut-slack-preview>
      </div>
    </div>
    <donut-modal-sections-actions
      button-text="Send"
      :is-save-disabled="!canSend"
      :loading-on-save="true"
      @save="send"
    />
  </div>
</template>

<script setup>
  import { ref, computed, inject, watch, onBeforeMount } from 'vue';
  import { useStore } from 'vuex';
  import useGifAttaching from 'composables/gif-attaching';
  import Network from 'lib/api/network';

  const $i18n = inject('$i18n');
  const { state, commit, dispatch } = useStore();

  const { shoutoutsProgram, pointsConfig } = state.modal.data;

  // Base Copy
  const pointsPhrase = (plural, hideEmoji) => {
    const phrase = plural ? pointsConfig.points_name_plural : pointsConfig.points_name;
    const emoji = hideEmoji ? '' : ` :${pointsConfig.emoji}:`;
    return phrase + emoji;
  };
  const numberedPointsPhrase = (number, hideEmoji) => `${number} ${pointsPhrase(number !== 1, hideEmoji)}`;
  const descriptionCopy = computed(() => {
    let copy = `DM a ${$i18n.t('shoutouts.shoutout', { count: 1 })} to make someone's day. `;
    copy += `A thoughtful message (plus a few ${pointsPhrase(true)}) goes a long way.`;
    return copy;
  });

  // Points Wallet
  const wallet = ref(null);
  const fetchWallet = () => {
    Network.get(`/points/wallets/${state.currentUser.id}`, {
      success: (data) => { wallet.value = data.wallet; },
      error: () => { dispatch('setErrorToast'); },
    });
  };

  // Recipients
  const recipientsSelection = ref([]);

  // Send As
  const userTokenReauth = computed(() => state.reauthing.slackAuth.send_as_user);
  const currentUserReauthRequired = computed(() => userTokenReauth.value && userTokenReauth.value.scopes.length > 0);
  const sendAsOptions = [
    { value: 'donut', label: 'Donut' },
    { value: 'user', label: state.currentUser.name, description: state.currentUser.email },
  ];
  const sendAsSelection = ref(sendAsOptions.find(o => o.value === (currentUserReauthRequired.value ? 'donut' : 'user')));
  const sendingAsUser = computed(() => sendAsSelection.value.value === 'user');

  // Message
  const message = ref('');
  const messagePlaceholder = 'Hi {{Recipient}}, thank you for your help finding that bug. You\'re so good at getting to the root of the problem!';
  const gifUrl = computed(() => state.modal.data.gifUrl);

  // Points
  const pointsHeader = `How many ${pointsPhrase(true)} do you want to send to each recipient?`;
  const pointsSelection = ref(1);
  const pointsSubtext = computed(() => {
    const timeframe = { weekly: 'week', monthly: 'month' }[shoutoutsProgram.renewal_period] || 'period';
    return `You have ${numberedPointsPhrase(wallet.value.current_givable, true)} left to give this ${timeframe}`;
  });
  const notEnoughPoints = computed(() => recipientsSelection.value.length * pointsSelection.value > wallet.value.current_givable);

  // Slack Preview
  const previewMessage = computed(() => {
    const effectiveMessage = message.value || messagePlaceholder;
    if (sendingAsUser.value) {
      return effectiveMessage;
    }
    const messagePrefix = `<@${state.currentUser.uid}> gave you a Shoutout with ${numberedPointsPhrase(pointsSelection.value)} via DM`;
    return `${messagePrefix}\n>${effectiveMessage}`;
  });

  // Progress Loading/Saving
  // // The nature of the flow between several modals is that we change the existing "open modal" information to be that of the new modal.
  // // Accordingly, we want to keep track of changes made in _this_ modal such that we can go back and forth without losing changes.
  const progressKeys = () => ({ recipientsSelection, sendAsSelection, message, gifUrl, pointsSelection });
  const { saveProgress, saveProgressAndMoveToGifModal } = useGifAttaching({ progressKeys });
  const saveProgressAndMoveToPermissionsModal = () => {
    saveProgress();
    dispatch('reauthing/openPermissionsModal', {
      contextName: 'send_as_user',
      source: 'Private Shoutouts Modal',
      // Prioritize the user scopes message over the channels_manage message.
      missingScope: userTokenReauth.value.scopes.find((scope) => scope.startsWith('user-')),
      previousModalOnCancel: true,
    });
  };

  // Submission
  const canSend = computed(() => {
    if (!wallet.value) return false;
    if (!message.value.trim()) return false;
    if (!recipientsSelection.value?.length) return false;
    if (notEnoughPoints.value) return false;
    return true;
  });
  const send = () => {
    commit('update', { key: 'formSubmitted', value: true });
    const params = {
      message: message.value,
      gif_url: gifUrl.value,
      points_attempted_per_recipient: pointsSelection.value,
      recipients: recipientsSelection.value.map(r => r.id),
      send_as_user: sendingAsUser.value,
    };
    const noun = $i18n.t('shoutouts.shoutout', { count: pointsSelection.value });
    Network.post('/shoutouts/shoutouts', { shoutouts_shoutout: params }, {
      success: () => {
        commit('updateModal', {
          visible: true,
          subcomponent: 'shoutouts-profiles-modals-private-shoutout-success',
          data: {
            reopenModalArgs: { ...state.modal, data: { pointsConfig, shoutoutsProgram } },
            recipients: recipientsSelection.value,
            pointsPhrase: numberedPointsPhrase(pointsSelection.value),
            sendAs: sendAsSelection.value.value,
          },
        });
      },
      error: () => { dispatch('setErrorToast', `Could not DM ${noun}`); },
      complete: () => {
        commit('update', { key: 'formSubmitted', value: false });
      },
    });
  };

  // Lifecycle Hooks
  onBeforeMount(() => {
    // Depending on where this modal is being opened, the currentUser's wallet might not be loaded in state anywhere.
    // Quickly load it on modal open.
    fetchWallet();
    // autocomplete doesn't use Vue.
    Donut.Args.otherVariables = ['Recipient'];
  });

  watch(sendAsSelection, ({ value }) => {
    if (value === 'user' && currentUserReauthRequired.value) {
      // Set it back to Donut so as to not immediately open the permissions modal again if you close out of it and reopen this modal
      sendAsSelection.value = sendAsOptions.find(o => o.value === 'donut');
      saveProgressAndMoveToPermissionsModal();
    }
  });
</script>
